import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import BrokenImageIcon from '@mui/icons-material/BrokenImageOutlined';
import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';


/**
 * Servera foto (/api/v1/.../image/{id}). Image endpointi prasa X-Client-Token, ko
 * <img src=> nepiesien, un relatīvais URL no servera atrisinātos pret admin domēnu -
 * tāpēc lādējam caur axios (baseURL + galvenes) kā blob.
 *
 * Query cachē pašu Blob, nevis ObjectURL - URL katrs komponents veido un atbrīvo pats,
 * lai cachētais ieraksts pēc un-mount nenorādītu uz jau atbrīvotu URL.
 */
function useApiImageUrl(url: string): { objectUrl: string | null; isLoading: boolean; isError: boolean } {
    const { data: blob, isLoading, isError } = useQuery({
        queryKey: ['admin-api-image-blob', url],
        queryFn: async () => {
            const resp = await api.get(url, { responseType: 'blob' });
            return resp.data as Blob;
        },
        staleTime: Infinity,
        gcTime: 5 * 60_000,
    });

    const objectUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
    useEffect(() => () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
    }, [objectUrl]);

    return { objectUrl, isLoading, isError };
}


/**
 * Kvadrātveida miniatūra; klikšķis atver pilnekrāna priekšskatījumu.
 */
export function ApiImageThumb({ url, size = 180, alt }: { url: string; size?: number; alt?: string }) {
    const { objectUrl, isLoading, isError } = useApiImageUrl(url);
    const [zoom, setZoom] = useState(false);

    return (
        <>
            <Box
                sx={{
                    width: size, height: size, flexShrink: 0, borderRadius: 1, overflow: 'hidden',
                    bgcolor: '#0001', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: objectUrl ? 'zoom-in' : 'default',
                }}
                onClick={() => objectUrl && setZoom(true)}
            >
                {isLoading && <CircularProgress size={24} />}
                {isError && (
                    <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        <BrokenImageIcon />
                        <Typography variant="caption" display="block">Neizdevās ielādēt</Typography>
                    </Box>
                )}
                {objectUrl && (
                    <Box
                        component="img"
                        src={objectUrl}
                        alt={alt ?? 'photo'}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                )}
            </Box>
            {zoom && objectUrl && (
                <Box
                    onClick={() => setZoom(false)}
                    sx={{
                        position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.85)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 1500, cursor: 'zoom-out', p: 4,
                    }}
                >
                    <img
                        src={objectUrl}
                        alt={alt ?? 'photo (preview)'}
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                </Box>
            )}
        </>
    );
}
